import { Button, Divider } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo, useState } from 'react';

import { Frame, FrameOptionConsolidated } from '../../../../types/Products';
import { ShopifyProductToCheckout } from '../../../../types/Purchasing';
import { customPropertyByKey } from '../../../../utils/product';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import { DrawerModule } from '../DrawerModule/DrawerModule';
import { ImageListItem } from '../ImageListItem/ImageListItem';
import { FormValues, FramePicker } from './FramePicker';

export interface OrderReviewProps {
  artistName: string;
  collectionTitle: string;
  frame: Frame | null;
  frameIncluded: boolean;
  frames: FrameOptionConsolidated[] | null;
  loading: boolean;
  onAddFrame: (entryId: string) => void;
  onRemoveFrame: (entryId: string) => void;
  onSelectFrame: (frameId: Frame) => void;
  onSelectFrameVariant: (variant: Frame) => void;
  onSubmit: () => void;
  orderItems: Record<string, ShopifyProductToCheckout>;
  shipping: number;
  storyblokProduct: ProductType;
  subtotal: number;
  tax: number;
  total: number;
}

export const OrderReview: FC<OrderReviewProps> = ({
  artistName,
  collectionTitle,
  frame,
  frameIncluded,
  frames,
  loading,
  onAddFrame,
  onRemoveFrame,
  onSelectFrame,
  onSelectFrameVariant,
  onSubmit,
  orderItems,
  shipping,
  storyblokProduct,
  subtotal,
  tax,
  total,
}) => {
  const { t } = useTranslation(['nftPrintService', 'orderDetails']);
  const [openDrawer, setOpenDrawer] = useState(false);

  const sortedItems = useMemo(
    () =>
      Object.entries(orderItems).sort(([, a], [, b]) => {
        const tokenA = customPropertyByKey(a.customProperties, 'token_id');
        const tokenB = customPropertyByKey(b.customProperties, 'token_id');
        return tokenA.localeCompare(tokenB, 'en', { numeric: true });
      }),
    [orderItems],
  );

  const framedIds = useMemo(
    () =>
      sortedItems
        .filter(([, item]) => item.frameBasketEntryId)
        .map(([entryId]) => entryId),
    [sortedItems],
  );

  const handleFramesSubmit = useCallback(
    (data: FormValues) => {
      const selectedOptions: string[] = [];
      if (data.option) {
        if (typeof data.option === 'string') {
          selectedOptions.push(data.option);
        } else {
          selectedOptions.push(...data.option);
        }
      }

      // Clear existing frames before adding new ones
      Object.keys(orderItems).forEach((id) => onRemoveFrame(id));
      selectedOptions.forEach(onAddFrame);

      setOpenDrawer(false);
    },
    [onAddFrame, onRemoveFrame, orderItems],
  );

  const frameHasUpgrade =
    frame &&
    frames &&
    (
      frames.find((frameOption) => frameOption.title === frame?.title)
        ?.variants ?? []
    ).length > 1;

  const overViewPreview = () => {
    const cartPreview: JSX.Element = (
      <div className="flex max-h-72 flex-col gap-3 overflow-y-auto">
        {sortedItems.map(([entryId, item]) => (
          <div
            key={entryId}
            className="flex items-center justify-between gap-3"
          >
            <ImageListItem
              className="min-w-0 flex-shrink"
              description={t('nftPrintService:tokenId', {
                id: customPropertyByKey(item.customProperties, 'token_id'),
              })}
              imageUris={customPropertyByKey(
                item.customProperties,
                '_token_img',
              ).split(',')}
              subtitle={collectionTitle}
              title={artistName}
              variant="product"
            />
            <div className="shrink-0">
              <ProductPrice price={item.product.price} />
            </div>
          </div>
        ))}
      </div>
    );

    if (!frames || frames.length < 0)
      return (
        <div className="flex flex-col rounded-sm border border-divider/20 bg-white pl-4 pr-4 pt-4">
          {cartPreview}
          <Divider className="mt-4" />
        </div>
      );

    return (
      <DrawerModule
        buttonText={
          framedIds.length > 0 && frame ? (
            <div>
              <div>
                {t('nftPrintService:selectedFrames', {
                  count: framedIds.length,
                  name: frame.title,
                })}
              </div>
              {frameHasUpgrade && <div>{frame.variant.title}</div>}
            </div>
          ) : (
            t('nftPrintService:selectFrames')
          )
        }
        drawer={
          <FramePicker
            artistName={artistName}
            collectionTitle={collectionTitle}
            defaultValues={{
              option: framedIds.length === 0 ? false : framedIds,
            }}
            frame={frame}
            frameIncluded={frameIncluded}
            frames={frames}
            onSelectFrame={onSelectFrame}
            onSelectFrameVariant={onSelectFrameVariant}
            onSubmit={handleFramesSubmit}
            orderItems={orderItems}
            storyblokProduct={storyblokProduct}
          />
        }
        isOpen={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        preview={cartPreview}
        suffix={
          framedIds.length > 0 && frame ? (
            <ProductPrice
              price={
                framedIds.length * frame.variant.consolidatedVariants[0].price
              }
            />
          ) : undefined
        }
      />
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      <h6 className="text-md font-bold">
        {t('nftPrintService:order.reviewTitle')}
      </h6>
      <div className="flex flex-col gap-8">
        {overViewPreview()}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between">
            <span>{t('orderDetails:subtotalText')}</span>
            <ProductPrice price={subtotal} />
          </div>
          <div className="flex justify-between">
            <span>{t('orderDetails:shippingText')}</span>
            <ProductPrice price={shipping} />
          </div>
          <div className="flex justify-between">
            <span>{t('orderDetails:taxtText')}</span>
            <ProductPrice price={tax} skipTaxCalculation />
          </div>
          <div className="flex justify-between font-bold">
            <span>{t('orderDetails:totalText')}</span>
            <ProductPrice price={total} skipTaxCalculation />
          </div>
        </div>
        <Button
          disabled={loading}
          fullWidth
          label={t('nftPrintService:order.reviewCta')}
          onClick={onSubmit}
          variant="primary"
        />
      </div>
    </div>
  );
};
